import { useEffect, useMemo, useRef, useState } from "react";
import { Globe, MagnifyingGlass, CaretDown, Check } from "@phosphor-icons/react";
import { listIanaTimezones, offsetLabelForTZ, detectBrowserTimezone } from "@/lib/timezones";

/**
 * TimezonePicker — searchable popover listing every IANA timezone the browser
 * supports (Intl.supportedValuesOf('timeZone'), ~400 zones). Falls back to a
 * curated list on legacy browsers.
 *
 * Props:
 *  - value          (string)   — current IANA zone (e.g. "America/Los_Angeles")
 *  - onChange(tz)   (fn)
 *  - testid         (string)   — root data-testid prefix, default "tz-picker"
 */
export default function TimezonePicker({ value, onChange, testid = "tz-picker" }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const rootRef = useRef(null);
  const all = useMemo(() => listIanaTimezones(), []);
  const browser = useMemo(() => detectBrowserTimezone(), []);

  // Close on outside click / Escape
  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => { if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false); };
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => { document.removeEventListener("mousedown", onDoc); document.removeEventListener("keydown", onKey); };
  }, [open]);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase().replace(/[\s_/-]+/g, "");
    if (!needle) return all;
    return all.filter((tz) => tz.toLowerCase().replace(/[\s_/-]+/g, "").includes(needle));
  }, [q, all]);

  const offset = offsetLabelForTZ(new Date(), value);

  return (
    <div ref={rootRef} className="relative" data-testid={testid}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center gap-2 px-3 py-2 border border-[var(--border)] bg-white text-left hover:bg-[var(--bg-sidebar)] transition-colors"
        data-testid={`${testid}-toggle`}
      >
        <Globe size={14} className="text-[var(--text-muted)] shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="font-mono text-[12px] font-semibold truncate">{value || "UTC"}</div>
        </div>
        <span className="font-mono text-[10px] text-[var(--text-muted)] shrink-0">{offset}</span>
        <CaretDown size={12} className={`text-[var(--text-muted)] shrink-0 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>

      {open && (
        <div
          className="absolute z-50 left-0 right-0 mt-1 bg-white border border-[var(--border)] shadow-lg max-h-[340px] flex flex-col"
          data-testid={`${testid}-popover`}
        >
          <div className="px-2 py-2 border-b border-[var(--border)] flex items-center gap-2">
            <MagnifyingGlass size={12} className="text-[var(--text-muted)]" />
            <input
              autoFocus
              type="text"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search timezones — try “Tokyo”, “London”, “PST”…"
              className="flex-1 text-[12px] outline-none bg-transparent"
              data-testid={`${testid}-search`}
            />
            <span className="font-mono text-[10px] text-[var(--text-muted)]">{filtered.length}</span>
          </div>

          {/* Quick picks */}
          {q.trim() === "" && (
            <div className="px-2 py-1.5 border-b border-[var(--border)] flex flex-wrap gap-1.5" data-testid={`${testid}-quick`}>
              {[browser, "UTC", "America/New_York", "America/Los_Angeles", "Europe/London", "Asia/Tokyo"]
                .filter((tz, i, a) => a.indexOf(tz) === i)
                .map((tz) => (
                  <button
                    key={tz}
                    type="button"
                    onClick={() => { onChange(tz); setOpen(false); }}
                    className={`px-2 py-0.5 text-[10px] font-mono border ${value === tz ? "border-[var(--accent)] bg-[var(--accent)] text-white" : "border-[var(--border)] hover:border-[var(--accent)]"}`}
                    data-testid={`${testid}-quick-${tz}`}
                  >
                    {tz === browser ? "Browser" : tz.split("/").pop().replace(/_/g, " ")}
                  </button>
                ))}
            </div>
          )}

          <div className="overflow-y-auto flex-1">
            {filtered.length === 0 && (
              <div className="px-3 py-4 text-[12px] text-[var(--text-muted)]">No matches.</div>
            )}
            {filtered.map((tz) => {
              const off = offsetLabelForTZ(new Date(), tz);
              const isSel = tz === value;
              return (
                <button
                  key={tz}
                  type="button"
                  onClick={() => { onChange(tz); setOpen(false); }}
                  className={`w-full flex items-center justify-between gap-2 px-3 py-1.5 text-left hover:bg-[var(--bg-sidebar)] ${isSel ? "bg-[var(--bg-sidebar)]" : ""}`}
                  data-testid={`${testid}-option-${tz}`}
                >
                  <span className="font-mono text-[12px] truncate flex items-center gap-1.5">
                    {isSel && <Check size={11} className="text-[var(--accent)]" />}
                    {tz}
                  </span>
                  <span className="font-mono text-[10px] text-[var(--text-muted)] shrink-0">{off}</span>
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
